import { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../store";

/**
 * Become an administrator, if this account is the one the deployment is configured to allow.
 *
 * <p>Registration always creates a regular user and nothing grants the admin role automatically, so a fresh
 * deployment has no administrator. This page is the way in: the backend compares the signed-in address
 * against `talklight.bootstrap-admin-email` and promotes the account only on an explicit request. Anyone can
 * open the page; only the configured address gets anywhere, and the attempt is a deliberate action rather
 * than something that happens quietly at sign-in.
 */
export function PromoteAdminPage(): JSX.Element {
  const { user, promoteToAdmin } = useApp();
  const [state, setState] = useState<"idle" | "working" | "done" | "refused">("idle");
  const [message, setMessage] = useState<string | null>(null);

  const alreadyAdmin = user?.role === "ADMIN";

  async function attempt(): Promise<void> {
    setState("working");
    setMessage(null);
    try {
      await promoteToAdmin();
      setState("done");
    } catch (error) {
      // A 403 is the expected answer for any account that is not the configured one, so it is reported as
      // a plain outcome rather than as a failure.
      setState("refused");
      setMessage(error instanceof Error ? error.message : "Request failed");
    }
  }

  return (
    <>
      <header className="app-header">
        <h1>Administrator access</h1>
        <Link className="link" to="/menu">
          Back
        </Link>
      </header>
      <div className="content">
        <div className="section-label">This account</div>
        <ul className="list">
          <li>
            <div className="row-main">
              <div className="row-title">{user?.displayName}</div>
              <div className="row-sub">
                {user?.email} {"\u00B7"} {user?.role.toLowerCase()}
              </div>
            </div>
          </li>
        </ul>

        {alreadyAdmin ? (
          <p className="hint">This account is already an administrator.</p>
        ) : (
          <>
            <p className="hint">
              If this deployment is configured to make this email address an administrator, you can claim the
              role here.
            </p>
            <button
              className="primary"
              type="button"
              disabled={state === "working"}
              onClick={() => void attempt()}
            >
              {state === "working" ? "Checking\u2026" : "Make this account an administrator"}
            </button>
          </>
        )}

        {state === "done" ? (
          <p className="hint">
            Done. <Link to="/admin">Open the admin pages</Link>.
          </p>
        ) : null}
        {state === "refused" ? (
          <p className="error">
            This account is not the configured administrator.
            {message ? ` (${message})` : null}
          </p>
        ) : null}
      </div>
    </>
  );
}
